// src/auth/recovery-service.ts
//
// The login-recovery suite's data layer: the five unauthenticated Identity
// endpoints that back forgot-password / reset-via-access-code / create-password
// (post-invite) / account-lockout. These are UNAUTHENTICATED calls (no bearer
// token) — they run before a session exists — so they use raw `fetch` against
// `env.apiBaseUrl`, exactly like `token-service.ts` posts to `/connect/token`.
// They deliberately do NOT go through `@/shared/api/api-client`, whose
// `buildHeaders` always attaches `Authorization: Bearer` + org/practice context
// (there is none yet).
//
// Flutter parity — every call mirrors `getUnauthenticApiClient()` usage in
// `scrips_msp1_flutter_shared/.../login_signup_module`:
//   - ForgotPassword   → forgot_password/data/datasource/forgot_password_data_source_impl.dart
//   - LoginByCode      → sign_up_with_access_code/data/datasources/sign_up_data_source_impl.dart
//   - SetPassword      → create_password/data/datasource/create_password_data_source_impl.dart
//   - CheckLockOutTime → login/data/datasources/login_data_source_impl.dart (checkLocked)
//
// NOTE: CheckUserExistence is deliberately NOT ported. That call requires a
// server-configured `EnvironmentKey` credential in its body; shipping that
// constant to the browser would let anyone probe arbitrary emails and discover
// their tenant/environment (an account-enumeration + credential-exposure
// oracle). Environment/tenant resolution stays server-side; the recovery flows
// here never need it.
import { env } from '@/config/env';

const ACCOUNT_BASE = `${env.apiBaseUrl}/api/Account`;

/** Raised when an Identity recovery call returns a non-2xx status, carrying the
 *  HTTP status so callers can branch (e.g. login → 400 → check lockout). */
export class RecoveryApiError extends Error {
  status: number;
  body: string;
  constructor(status: number, body: string) {
    super(`recovery request failed: ${status}`);
    this.name = 'RecoveryApiError';
    this.status = status;
    this.body = body;
  }
}

/** JSON POST helper for the unauthenticated Account endpoints. `extraHeaders`
 *  carries per-endpoint headers (e.g. ForgotPassword's `AppType: Provider`). */
async function postJson<T>(
  path: string,
  body: unknown,
  extraHeaders?: Record<string, string>,
): Promise<{ status: number; data: T | null }> {
  const res = await fetch(`${ACCOUNT_BASE}${path}`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...extraHeaders },
    body: JSON.stringify(body),
  });
  const text = await res.text();
  if (!res.ok) throw new RecoveryApiError(res.status, text);
  return { status: res.status, data: (text ? (JSON.parse(text) as T) : null) };
}

/** POST /api/Account/ForgotPassword — email the user a reset access code.
 *  Flutter: `ForgotPasswordDataSourceImpl.forgotPassword` posts `{ email }` with
 *  the `AppType` header ("Provider" for PA). Response body is ignored (Success). */
export async function forgotPassword(email: string): Promise<void> {
  // AppType is the app-surface discriminator the backend uses to pick the
  // correct email template / user pool. PA === "Provider" (Flutter
  // `currentAppType == AppType.PA ? "Provider"`).
  await postJson('/ForgotPassword', { email }, { AppType: 'Provider' });
}

/** {@link loginByCode} response — the access-code exchange returns the one-shot
 *  `passwordResetToken` that {@link setPassword} must present. Flutter
 *  `SignupByCodeResponse` (passwordResetToken + emailAddress). */
export interface LoginByCodeResult {
  passwordResetToken: string | null;
  emailAddress: string | null;
}

/** POST /api/Account/LoginByCode — verify the emailed access code and mint the
 *  password-reset token. Flutter: `SignUpDataSourceImpl.signUpByCode` posts
 *  `{ email, code, isresetpassword: true }`. The `isresetpassword` flag is
 *  verbatim (lowercase) — the backend keys off that exact field name. */
export async function loginByCode(email: string, code: string): Promise<LoginByCodeResult> {
  const { data } = await postJson<LoginByCodeResult>('/LoginByCode', {
    email,
    code,
    isresetpassword: true,
  });
  return {
    passwordResetToken: data?.passwordResetToken ?? null,
    emailAddress: data?.emailAddress ?? null,
  };
}

/** POST /api/Account/SetPassword — commit the new password against the reset
 *  token. Backend `SetPasswordViewModel` accepts only { Email, Password,
 *  PasswordResetToken }. The Flutter client also sends a `confirmPassword`
 *  mirror, but the backend ignores it — sending it duplicates the plaintext
 *  password on the wire for no contract benefit, so we omit it and match the
 *  real DTO. Confirm-match is enforced client-side (password-policy). Used by
 *  BOTH the reset flow and post-invite create-password. Response body is
 *  ignored (Success). */
export async function setPassword(params: {
  email: string;
  password: string;
  passwordResetToken: string;
}): Promise<void> {
  await postJson('/SetPassword', {
    email: params.email,
    password: params.password,
    passwordResetToken: params.passwordResetToken,
  });
}

/** {@link checkLockOutTime} result — the backend's lockout window. Flutter
 *  `LockedTime` (time + minute). `minute` present ⇒ the account is locked and
 *  `minute` is the remaining lockout duration. */
export interface LockoutInfo {
  /** ISO timestamp the backend returns with the lock window. */
  time: string | null;
  /** Remaining lockout minutes (fractional). `null` ⇒ not locked. */
  minute: number | null;
}

/** GET /api/Account/CheckLockOutTime?emailId=… — is this account locked, and for
 *  how much longer? Flutter: `LoginDataSourceImpl.checkLocked` treats **only a
 *  201** as "locked" (returns the LockedTime); any other status ⇒ not locked
 *  (empty LockedTime). Network errors are swallowed to "not locked" so a failed
 *  probe never blocks the user. */
export async function checkLockOutTime(email: string): Promise<LockoutInfo> {
  try {
    const res = await fetch(
      `${ACCOUNT_BASE}/CheckLockOutTime?emailId=${encodeURIComponent(email)}`,
      { method: 'GET', headers: { Accept: 'application/json' } },
    );
    // Flutter: `if (response.statusCode == 201) return lockedTimeFromJson(...)`
    // else `return LockedTime()`. 201 is the ONLY locked signal.
    if (res.status !== 201) return { time: null, minute: null };
    const text = await res.text();
    const data = text ? (JSON.parse(text) as Partial<LockoutInfo>) : {};
    return { time: data.time ?? null, minute: data.minute ?? null };
  } catch {
    return { time: null, minute: null };
  }
}
